import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { parseSRT } from "@/lib/srt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Calendar } from "@/components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { formatDateIt } from "@/lib/date";
import { CATEGORIES } from "@/lib/categories";
import { COUNTRIES, COUNTRY_CODE_BY_NAME } from "@/lib/countries";
import { LANGUAGES } from "@/lib/languages";
import {
  ArrowLeft, Sparkles, Loader2, Plus, Trash2, Save, Wand2, Upload, ListChecks,
  Check, ChevronsUpDown, Calendar as CalendarIcon, Captions, Download, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const EMPTY = {
  title: "", leader: "", country: "", country_code: "", theme: "", original_language: "",
  event_date: "", event_location: "", youtube_url: "", source_video_url: "", source_url: "", source_name: "",
  featured: false, status: "draft", summary: [], paragraphs: [],
};

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [originalText, setOriginalText] = useState("");
  const [italianText, setItalianText] = useState("");
  const [aligning, setAligning] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editing);
  const [countryOpen, setCountryOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [savedId, setSavedId] = useState(id || null);
  const [autoState, setAutoState] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [subsOpen, setSubsOpen] = useState(false);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsAvailable, setSubsAvailable] = useState([]);
  const [subsLang, setSubsLang] = useState("");
  const [subsActing, setSubsActing] = useState("");
  const [subsUrl, setSubsUrl] = useState("");
  const [translating, setTranslating] = useState(false);
  const [transProgress, setTransProgress] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const savingRef = useRef(false);
  const firstRunRef = useRef(true);
  const autoTimer = useRef(null);

  useEffect(() => {
    if (editing) {
      api.get(`/admin/speeches/${id}`).then((r) => {
        setForm({ ...EMPTY, ...r.data });
        setOriginalText((r.data.paragraphs || []).map((p) => p.original).join("\n"));
        setItalianText((r.data.paragraphs || []).map((p) => p.italian).join("\n"));
      }).finally(() => setLoaded(true));
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const prefillFromLink = async () => {
    if (!importUrl.trim()) { toast.error("Incolla un link YouTube"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/youtube/prefill", { youtube_url: importUrl, use_ai: true });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        leader: data.leader || f.leader,
        country: data.country || f.country,
        country_code: data.country_code || f.country_code,
        theme: data.theme || f.theme,
        original_language: data.original_language || f.original_language,
        event_date: data.event_date || f.event_date,
        youtube_url: data.youtube_url || f.youtube_url,
        summary: data.summary?.length ? data.summary : f.summary,
      }));
      if (data.already_exists) toast.warning("Attenzione: questo video risulta già presente nell'archivio.");
      else toast.success("Campi compilati dal video. Rivedi e completa.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Importazione non riuscita.");
    } finally {
      setImporting(false);
    }
  };

  const autoSave = async () => {
    if (savingRef.current) return;
    if (!form.title?.trim() || !form.leader?.trim()) return;
    savingRef.current = true;
    setAutoState("saving");
    const payload = { ...form, status: form.status || "draft", summary: (form.summary || []).filter((s) => s.trim()) };
    try {
      if (savedId) {
        await api.put(`/admin/speeches/${savedId}`, payload);
      } else {
        const r = await api.post("/admin/speeches", payload);
        setSavedId(r.data.id);
      }
      setAutoState("saved");
      setLastSavedAt(new Date());
    } catch {
      setAutoState("error");
    } finally {
      savingRef.current = false;
    }
  };

  // Salvataggio automatico in bozza (debounce) mentre si scrive
  useEffect(() => {
    if (!loaded) return;
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => { autoSave(); }, 1800);
    return () => clearTimeout(autoTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loaded]);

  // Avvisa se si chiude la pagina con un salvataggio in corso
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (savingRef.current || autoState === "saving") { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [autoState]);

  const onCountryChange = (name) => {
    setForm((f) => ({ ...f, country: name, country_code: COUNTRY_CODE_BY_NAME[name] || "" }));
  };

  const parseDate = (s) => {
    const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
  };
  const toISODate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const loadSubsAvailable = async (url) => {
    if (!url?.trim()) { toast.error("Inserisci un link YouTube"); return; }
    setSubsLoading(true);
    setSubsAvailable([]);
    setSubsLang("");
    try {
      const { data } = await api.post("/admin/youtube/subtitles", { youtube_url: url, lang: "" });
      setSubsAvailable(data.available || []);
      setSubsLang(data.lang || data.available?.[0]?.lang || "");
      if (!data.available?.length) toast.info("Nessun sottotitolo disponibile per questo video.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossibile leggere i sottotitoli.");
    } finally {
      setSubsLoading(false);
    }
  };

  const openSubs = () => {
    const u = (form.source_video_url?.trim() || form.youtube_url?.trim() || "");
    setSubsUrl(u);
    setSubsAvailable([]);
    setSubsLang("");
    setSubsOpen(true);
    if (u) loadSubsAvailable(u);
  };

  const fetchSubs = async () => {
    const { data } = await api.post("/admin/youtube/subtitles", { youtube_url: subsUrl, lang: subsLang });
    if (!data.text) throw new Error("empty");
    return data;
  };

  const subsTo = async (target) => {
    setSubsActing(target);
    try {
      const d = await fetchSubs();
      if (target === "orig") { setOriginalText(d.text); toast.success("Sottotitoli inseriti nel testo originale"); setSubsOpen(false); }
      else if (target === "it") {
        setItalianText(d.text);
        toast.success("Sottotitoli inseriti nella traduzione italiana");
        setSubsOpen(false);
        const hasSummary = (form.summary || []).some((s) => s.trim());
        if (!hasSummary) generateSummaryFrom(d.text);
      }
      else {
        const blob = new Blob([d.srt], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${d.video_id}-${d.lang}.srt`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("Sottotitoli non disponibili per questa lingua.");
    } finally {
      setSubsActing("");
    }
  };

  const translateToOriginal = async () => {
    if (!italianText.trim()) { toast.error("Non c'è testo italiano da tradurre"); return; }
    if (!form.original_language?.trim()) { toast.error("Imposta prima la 'Lingua originale' nella scheda"); return; }
    const CHUNK = 1800;
    const paras = italianText.split(/\n/);
    const chunks = [];
    let cur = "";
    for (const p of paras) {
      if (cur && cur.length + p.length + 1 > CHUNK) { chunks.push(cur); cur = p; }
      else { cur = cur ? cur + "\n" + p : p; }
    }
    if (cur.trim()) chunks.push(cur);
    if (!chunks.length) chunks.push(italianText);

    setTranslating(true);
    setTransProgress({ done: 0, total: chunks.length });
    try {
      const out = [];
      for (let i = 0; i < chunks.length; i++) {
        const { data } = await api.post("/admin/ai/translate", { text: chunks[i], target_language: form.original_language });
        out.push(data.text);
        setOriginalText(out.join("\n"));
        setTransProgress({ done: i + 1, total: chunks.length });
      }
      toast.success(`Testo tradotto in ${form.original_language}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Traduzione non riuscita.");
    } finally {
      setTranslating(false);
      setTransProgress(null);
    }
  };

  const onSrtUpload = async (e, which) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseSRT(text);
    if (which === "it") setItalianText(parsed);
    else setOriginalText(parsed);
    toast.success("File SRT caricato e convertito in testo");
  };

  const runAlign = async () => {
    if (!originalText.trim() || !italianText.trim()) {
      toast.error("Inserisci sia il testo originale sia la traduzione italiana");
      return;
    }
    setAligning(true);
    try {
      const { data } = await api.post("/admin/ai/align", {
        original_text: originalText,
        italian_text: italianText,
      });
      set("paragraphs", data.paragraphs);
      toast.success(data.fallback ? "Allineamento base creato (AI non disponibile), rivedilo" : "Paragrafi allineati con l'AI");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAligning(false);
    }
  };

  const generateSummaryFrom = async (text) => {
    if (!text?.trim()) return;
    setSummarizing(true);
    try {
      const { data } = await api.post("/admin/ai/summary", { italian_text: text });
      if (data.summary?.length) {
        set("summary", data.summary);
        toast.success("Punti chiave generati automaticamente");
      }
    } catch (err) {
      console.error("Auto-riassunto non riuscito:", err);
      /* silenzioso in UI: l'utente può sempre usare il pulsante manuale */
    } finally {
      setSummarizing(false);
    }
  };

  const runSummary = async () => {
    const text = italianText.trim() || (form.paragraphs || []).map((p) => p.italian).join("\n");
    if (!text) {
      toast.error("Serve del testo italiano per generare il riassunto");
      return;
    }
    setSummarizing(true);
    try {
      const { data } = await api.post("/admin/ai/summary", { italian_text: text });
      set("summary", data.summary);
      toast.success("Riassunto generato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSummarizing(false);
    }
  };

  const updatePair = (i, key, val) => {
    const next = [...form.paragraphs];
    next[i] = { ...next[i], [key]: val };
    set("paragraphs", next);
  };
  const addPair = () => set("paragraphs", [...form.paragraphs, { original: "", italian: "", timestamp: "" }]);
  const removePair = (i) => set("paragraphs", form.paragraphs.filter((_, idx) => idx !== i));

  const updateSummary = (i, val) => {
    const next = [...form.summary];
    next[i] = val;
    set("summary", next);
  };
  const addSummary = () => set("summary", [...form.summary, ""]);
  const removeSummary = (i) => set("summary", form.summary.filter((_, idx) => idx !== i));

  const save = async (status) => {
    if (!form.title || !form.leader) {
      toast.error("Titolo e leader sono obbligatori");
      return;
    }
    clearTimeout(autoTimer.current);
    setSaving(true);
    savingRef.current = true;
    const payload = { ...form, status, summary: form.summary.filter((s) => s.trim()) };
    try {
      if (savedId) await api.put(`/admin/speeches/${savedId}`, payload);
      else {
        const r = await api.post("/admin/speeches", payload);
        setSavedId(r.data.id);
      }
      toast.success(status === "published" ? "Discorso pubblicato" : "Bozza salvata");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  if (!loaded) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Editor discorso" robots="noindex, nofollow, noarchive" suppressCanonical />
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-xl z-40">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground min-w-[130px] text-right" data-testid="autosave-status">
              {autoState === "saving" && (<span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Salvataggio…</span>)}
              {autoState === "saved" && lastSavedAt && (<span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-primary" /> Salvato {lastSavedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>)}
              {autoState === "error" && (<span className="text-accent">Salvataggio non riuscito</span>)}
            </span>
            <Button variant="outline" onClick={() => save("draft")} disabled={saving} className="rounded-sm gap-1.5" data-testid="save-draft-btn">
              <Save className="h-4 w-4" /> Bozza
            </Button>
            <Button onClick={() => save("published")} disabled={saving} className="rounded-sm gap-1.5" data-testid="publish-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Pubblica
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-8 space-y-10">
        <h1 className="font-serif text-3xl font-bold">{editing ? "Modifica discorso" : "Nuovo discorso"}</h1>

        {!editing && (
          <section className="border border-dashed border-primary/40 rounded-sm p-4 bg-primary/5 space-y-3" data-testid="prefill-panel">
            <div className="flex items-center gap-2">
              <Youtube className="h-5 w-5 text-accent" />
              <h2 className="text-sm font-semibold">Importa da link YouTube</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              Incolla il link del video: titolo, data, Paese, leader, tema e punti chiave verranno compilati in automatico con l'IA. Potrai poi rivedere e completare i campi.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && prefillFromLink()}
                placeholder="https://www.youtube.com/watch?v=..."
                className="rounded-sm flex-1"
                data-testid="prefill-url-input"
              />
              <Button onClick={prefillFromLink} disabled={importing} className="rounded-sm gap-1.5 shrink-0" data-testid="prefill-btn">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                Compila automaticamente
              </Button>
            </div>
          </section>
        )}

        {/* Metadata */}
        <section className="space-y-5">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Informazioni</h2>
          <Field label="Titolo (italiano) *">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-sm" data-testid="field-title" placeholder="Discorso all'Assemblea Generale ONU" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Leader *">
              <Input value={form.leader} onChange={(e) => set("leader", e.target.value)} className="rounded-sm" data-testid="field-leader" placeholder="Joe Biden" />
            </Field>
            <Field label="Paese">
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between rounded-sm font-normal"
                    data-testid="field-country"
                  >
                    {form.country || <span className="text-muted-foreground">Scegli un Paese</span>}
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command>
                    <CommandInput placeholder="Cerca un Paese..." data-testid="country-search" />
                    <CommandList>
                      <CommandEmpty>Nessun Paese trovato.</CommandEmpty>
                      <CommandGroup>
                        {COUNTRIES.map((c) => (
                          <CommandItem
                            key={c.code}
                            value={c.name}
                            data-testid={`country-option-${c.code}`}
                            onSelect={() => { onCountryChange(c.name); setCountryOpen(false); }}
                          >
                            <Check className={cn("h-4 w-4", form.country === c.name ? "opacity-100" : "opacity-0")} />
                            {c.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Codice Paese (ISO)" hint="Compilato automaticamente dal Paese scelto">
              <Input value={form.country_code} readOnly disabled className="rounded-sm bg-muted/50" data-testid="field-country-code" placeholder="—" />
            </Field>
            <Field label="Categoria" hint="Come le playlist del canale">
              <Select value={form.theme || undefined} onValueChange={(v) => set("theme", v)}>
                <SelectTrigger className="rounded-sm" data-testid="field-theme"><SelectValue placeholder="Scegli una categoria" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Lingua originale">
              <Select value={form.original_language || undefined} onValueChange={(v) => set("original_language", v)}>
                <SelectTrigger className="rounded-sm" data-testid="field-language"><SelectValue placeholder="Scegli una lingua" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Data evento" hint="Scegli la data dal calendario">
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 rounded-sm font-normal"
                    data-testid="field-date"
                  >
                    <CalendarIcon className="h-4 w-4 opacity-70 shrink-0" />
                    {form.event_date
                      ? formatDateIt(form.event_date)
                      : <span className="text-muted-foreground">Scegli una data</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={parseDate(form.event_date)}
                    onSelect={(d) => { if (d) { set("event_date", toISODate(d)); setDateOpen(false); } }}
                    captionLayout="dropdown-buttons"
                    fromYear={2000}
                    toYear={new Date().getFullYear()}
                    defaultMonth={parseDate(form.event_date)}
                    locale={itLocale}
                    classNames={{
                      vhidden: "sr-only",
                      caption_label: "hidden",
                      caption_dropdowns: "flex justify-center gap-2",
                      dropdown: "bg-background border border-border rounded-sm text-sm px-2 py-1 capitalize focus:outline-none focus:ring-1 focus:ring-primary",
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Luogo evento">
              <Input value={form.event_location} onChange={(e) => set("event_location", e.target.value)} className="rounded-sm" data-testid="field-location" placeholder="New York, ONU" />
            </Field>
          </div>
          <Field label="Link YouTube" hint="Il video resta su YouTube ed è incorporato nella pagina">
            <Input value={form.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} className="rounded-sm" data-testid="field-youtube" placeholder="https://www.youtube.com/watch?v=..." />
          </Field>
          <Field label="Link video originale (fonte)" hint="Il video originale non pubblicato da te: da qui puoi scaricare i sottotitoli nella lingua originale">
            <Input value={form.source_video_url} onChange={(e) => set("source_video_url", e.target.value)} className="rounded-sm" data-testid="field-source-video" placeholder="https://www.youtube.com/watch?v=... (video originale)" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nome fonte ufficiale">
              <Input value={form.source_name} onChange={(e) => set("source_name", e.target.value)} className="rounded-sm" data-testid="field-source-name" placeholder="The White House" />
            </Field>
            <Field label="Link fonte ufficiale">
              <Input value={form.source_url} onChange={(e) => set("source_url", e.target.value)} className="rounded-sm" data-testid="field-source-url" placeholder="https://whitehouse.gov/..." />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="field-featured" />
            <Label>Metti in evidenza in homepage</Label>
          </div>
        </section>

        <Separator />

        {/* AI text ingestion */}
        <section className="space-y-5">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            Testi & allineamento AI
          </h2>
          <div className="flex flex-wrap items-center gap-2 -mt-2">
            <Button onClick={openSubs} variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="fetch-subs-btn">
              <Captions className="h-4 w-4" /> Sottotitoli da YouTube
            </Button>
            <span className="text-xs text-muted-foreground">Dal video della scheda o dal video originale della fonte: inserisci i sottotitoli qui sotto o esporta il file .srt.</span>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Testo originale</Label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={translateToOriginal} disabled={translating} className="text-xs text-primary cursor-pointer inline-flex items-center gap-1 disabled:opacity-50" data-testid="translate-to-original-btn">
                    {translating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    {translating && transProgress
                      ? `Traduzione ${transProgress.done}/${transProgress.total}…`
                      : `Traduci dall'IT${form.original_language ? ` → ${form.original_language}` : ""}`}
                  </button>
                  <label className="text-xs text-primary cursor-pointer inline-flex items-center gap-1">
                    <Upload className="h-3 w-3" /> SRT
                    <input type="file" accept=".srt,.txt" className="hidden" onChange={(e) => onSrtUpload(e, "orig")} data-testid="upload-srt-orig" />
                  </label>
                </div>
              </div>
              <Textarea value={originalText} onChange={(e) => setOriginalText(e.target.value)} className="rounded-sm min-h-48 font-mono text-sm" data-testid="original-text-input" placeholder="Incolla qui il testo nella lingua originale (un paragrafo per riga)..." />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Traduzione italiana</Label>
                <label className="text-xs text-primary cursor-pointer inline-flex items-center gap-1">
                  <Upload className="h-3 w-3" /> SRT ITA
                  <input type="file" accept=".srt,.txt" className="hidden" onChange={(e) => onSrtUpload(e, "it")} data-testid="upload-srt-it" />
                </label>
              </div>
              <Textarea value={italianText} onChange={(e) => setItalianText(e.target.value)} className="rounded-sm min-h-48 text-sm" data-testid="italian-text-input" placeholder="Carica il file SRT italiano oppure incolla il testo tradotto..." />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={runAlign} disabled={aligning} variant="outline" className="rounded-sm gap-1.5" data-testid="align-btn">
              {aligning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Allinea paragrafi con AI
            </Button>
            <Button onClick={runSummary} disabled={summarizing} variant="outline" className="rounded-sm gap-1.5" data-testid="summary-btn">
              {summarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} Genera riassunto AI
            </Button>
          </div>
        </section>

        {/* Paragraph pairs editor */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
              Paragrafi allineati ({form.paragraphs.length})
            </h2>
            <Button size="sm" variant="ghost" onClick={addPair} className="rounded-sm gap-1" data-testid="add-pair-btn">
              <Plus className="h-4 w-4" /> Aggiungi
            </Button>
          </div>
          {form.paragraphs.map((p, i) => (
            <div key={i} className="border border-border rounded-sm p-4 space-y-3" data-testid={`pair-${i}`}>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-muted-foreground">#{i + 1}</span>
                <Input value={p.timestamp} onChange={(e) => updatePair(i, "timestamp", e.target.value)} className="rounded-sm h-8 w-28 font-mono text-xs" placeholder="00:12" data-testid={`pair-ts-${i}`} />
                <Button size="icon" variant="ghost" className="rounded-sm text-accent ml-auto h-8 w-8" onClick={() => removePair(i)} data-testid={`remove-pair-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Textarea value={p.original} onChange={(e) => updatePair(i, "original", e.target.value)} className="rounded-sm text-sm min-h-20" placeholder="Originale" data-testid={`pair-orig-${i}`} />
                <Textarea value={p.italian} onChange={(e) => updatePair(i, "italian", e.target.value)} className="rounded-sm text-sm min-h-20" placeholder="Italiano" data-testid={`pair-it-${i}`} />
              </div>
            </div>
          ))}
        </section>

        {/* Summary editor */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
              Punti chiave ({form.summary.length})
            </h2>
            <Button size="sm" variant="ghost" onClick={addSummary} className="rounded-sm gap-1" data-testid="add-summary-btn">
              <Plus className="h-4 w-4" /> Aggiungi
            </Button>
          </div>
          {form.summary.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-mono text-primary shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <Input value={s} onChange={(e) => updateSummary(i, e.target.value)} className="rounded-sm" data-testid={`summary-${i}`} />
              <Button size="icon" variant="ghost" className="rounded-sm text-accent h-9 w-9 shrink-0" onClick={() => removeSummary(i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </section>
      </div>

      <Dialog open={subsOpen} onOpenChange={setSubsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Captions className="h-5 w-5 text-primary" /> Sottotitoli da YouTube</DialogTitle>
            <DialogDescription>Incolla il link del video (quello di SmartNews o l'originale della fonte), scegli la lingua e inserisci i sottotitoli nel testo oppure scarica il file .srt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Link del video YouTube</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={subsUrl}
                  onChange={(e) => setSubsUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... (SmartNews o video originale)"
                  className="rounded-sm"
                  data-testid="subs-url-input"
                />
                <Button onClick={() => loadSubsAvailable(subsUrl)} disabled={subsLoading || !subsUrl.trim()} variant="outline" className="rounded-sm gap-1.5 shrink-0" data-testid="subs-find-btn">
                  {subsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Captions className="h-4 w-4" />} Trova
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-1.5">
                {form.youtube_url?.trim() && subsUrl !== form.youtube_url.trim() && (
                  <button type="button" onClick={() => { setSubsUrl(form.youtube_url.trim()); loadSubsAvailable(form.youtube_url.trim()); }} className="text-xs text-primary hover:underline" data-testid="subs-use-card-video">
                    Usa il video della scheda (SmartNews)
                  </button>
                )}
                {form.source_video_url?.trim() && subsUrl !== form.source_video_url.trim() && (
                  <button type="button" onClick={() => { setSubsUrl(form.source_video_url.trim()); loadSubsAvailable(form.source_video_url.trim()); }} className="text-xs text-primary hover:underline" data-testid="subs-use-original-video">
                    Usa il video originale
                  </button>
                )}
              </div>
            </div>
          {subsLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : subsAvailable.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Nessun sottotitolo caricato. Incolla un link e premi "Trova".</p>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Lingua dei sottotitoli</Label>
                <Select value={subsLang} onValueChange={setSubsLang}>
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="subs-lang-select"><SelectValue placeholder="Scegli la lingua" /></SelectTrigger>
                  <SelectContent>
                    {subsAvailable.map((a) => (
                      <SelectItem key={a.lang} value={a.lang}>{a.name}{a.auto ? " · automatici" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => subsTo("orig")} disabled={!!subsActing || !subsLang} className="rounded-sm gap-1.5" data-testid="subs-to-original">
                  {subsActing === "orig" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Nel testo originale
                </Button>
                <Button onClick={() => subsTo("it")} disabled={!!subsActing || !subsLang} variant="outline" className="rounded-sm gap-1.5" data-testid="subs-to-italian">
                  {subsActing === "it" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Nella traduzione IT
                </Button>
                <Button onClick={() => subsTo("srt")} disabled={!!subsActing || !subsLang} variant="outline" className="rounded-sm gap-1.5" data-testid="subs-download">
                  {subsActing === "srt" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Scarica .srt
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Suggerimento: i sottotitoli automatici possono contenere piccoli errori di trascrizione. Rivedili prima di allinearli.
              </p>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
